import { LogisticForm } from '@/components'
import { ModalOrderInfosChangeWarning } from '@/components/modal-order-infos-change-warning'
import { onErrorMessage, onSuccessMessage } from '@/helpers'
import { QUERIES_ADMIN, useOrderCenterSaleOrderUpdate } from '@/services'
import { SaleOrderProps } from '@/types'
import { Button, DrawerStepper } from '@mercai/clever'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getChangedTypes,
  getFormattedDataToPayload,
} from './order-logistic-drawer-form.constants'
import { OrderLogisticDrawerFormProps } from './order-logistic-drawer-form.type'

export const OrderLogisticDrawerForm = ({
  isOpen,
  initialValues,
  onHandleClose,
  initialTab = 'transport',
}: OrderLogisticDrawerFormProps) => {
  const { t } = useTranslation('screenOrderCenterPage')

  const queryClient = useQueryClient()

  const [values, setValues] = useState(initialValues as SaleOrderProps)

  const [isOpenValidationModal, setIsOpenValidationModal] = useState(false)

  const changedTypes = useMemo(
    () => getChangedTypes(initialValues, values),
    [values],
  )

  const { mutateAsync } = useOrderCenterSaleOrderUpdate(
    initialValues?.uuid || '',
  )

  const onSucess = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, initialValues?.uuid],
    })
  }

  const onSubmit = async () => {
    const response = await mutateAsync(getFormattedDataToPayload(values))

    if (response.success) {
      onSucess()

      onSuccessMessage(
        t('orderCenterShowTabProductsSuccessMessageTitle'),
        t('orderCenterShowTabProductsSuccessMessageDescription'),
      )

      onHandleClose()
    } else {
      onErrorMessage(
        t('orderCenterShowTabProductsErrorMessageTitle'),
        t('orderCenterShowTabProductsErrorMessageDescription'),
      )
    }
  }

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues as SaleOrderProps)
    } else {
      setValues({} as SaleOrderProps)
      setIsOpenValidationModal(false)
    }
  }, [isOpen])

  return (
    <>
      <DrawerStepper
        isOpen={isOpen}
        onHandleOpen={onHandleClose}
        initialStep="root"
        translations={{ optional: '' }}
        title={t('orderCenterShowTabClientLogisticDrawerFormTitle')}
        steps={{
          root: {
            backNavigate: 'root',
            isOptional: false,
            component: () => (
              <LogisticForm
                initialTab={initialTab}
                values={values}
                onChange={(values) => setValues(values)}
                onRefetch={onSucess}
              />
            ),
            footer: () => (
              <>
                <Button variant="secondary" onClick={onHandleClose}>
                  {t('formTransactionStepFormBack')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() =>
                    changedTypes?.length > 0
                      ? setIsOpenValidationModal(true)
                      : onSubmit()
                  }
                >
                  {t('formTransactionStepFormSubmit')}
                </Button>
              </>
            ),
          },
        }}
      />
      <ModalOrderInfosChangeWarning
        isOpen={isOpenValidationModal}
        onClose={() => setIsOpenValidationModal(false)}
        saleOrderUuid={initialValues?.uuid}
        onSave={onSubmit}
        types={changedTypes}
      />
    </>
  )
}
